---
title: Architecture
description: The whole system end to end, on one page.
---

BibaCV is three tiers. An operator app that people use, an object store that
holds each night's recordings, and one GPU machine per store that does the
actual watching. They are connected by exactly three kinds of traffic:
settings going down, video going sideways, and reports coming back up.

<ArchitectureDiagram />

## Tier 1: the operator app

The app you use in the browser. Two services, deployable anywhere:

- **Frontend** (port 3001). A TanStack Start app served by Bun. It renders
  the store settings, the zone editor, the upload page, run history and
  reports. It talks only to the gateway.
- **Gateway API** (port 3000). A Bun service using oRPC for typed procedures
  and Better Auth for email and password accounts. It owns two pieces of
  state: **Postgres** (accounts plus every store's configuration, cameras,
  calibration and zones) and **Redis** (sessions and caching). It also signs
  the presigned URLs the browser uses to upload recordings.

The gateway is the only writer of store configuration. When you press save on
a settings page, the gateway validates the change, stores it, and pushes it to
the store's GPU machine over HTTP with a bearer token. The GPU machine's own
environment always wins at run time, so a machine can be pinned locally
without the app fighting it.

## Tier 2: the object store

One bucket path per store and per night. The layout is a fixed contract:

```
<bucket>/<store_id>/<date>/videos/<camera>.<ext>
<bucket>/<store_id>/<date>/camInfo/<camera>.yml
<bucket>/<store_id>/<date>/session.yml        (optional)
```

Three routes fill it, chosen per store in the Ingest settings:

- **S3.** The store's recorder or a sync job writes the night directly.
- **SFTP.** The GPU machine logs in and pulls the night down itself.
- **Upload.** A person uploads the night from this app. The browser puts the
  files at these exact keys using presigned URLs, so downstream stages cannot
  tell the difference.

## Tier 3: the store GPU box

One machine per store, holding everything that touches video:

- **Control plane** (port 8000). A small HTTP service the gateway drives. It
  receives configuration pushes, starts and reports on runs, and triggers
  calibration. It is the only way in.
- **The nightly chain.** Four batch stages run in order: fetch, perception,
  analytics, review. This is a batch system, not a server. Cron starts the
  chain, and when it ends the GPU is idle. The chain is described stage by
  stage in [The nightly pipeline](/docs/pipeline).
- **Kafka** carries per-frame tracking from perception to analytics.
- **MQTT** carries identity negotiation between cameras during perception.
- **The calibration service** (AMC) runs outside the nightly chain. It works
  out where each camera stands on the floor plan, once, after cameras are
  installed or moved.

## The three kinds of traffic

| Traffic        | Path                                             | Protocol                               |
| -------------- | ------------------------------------------------ | -------------------------------------- |
| Settings down  | Gateway to control plane                         | HTTP + bearer token                    |
| Video sideways | Recorder or browser to bucket, bucket to GPU box | S3 / SFTP / presigned PUT, then rclone |
| Reports up     | Control plane to gateway to browser              | HTTP                                   |

## Trust boundaries

- The browser only ever talks to the gateway and, for uploads, to the bucket
  with a short-lived presigned URL.
- The gateway holds no video and no ML state. Losing it loses accounts and
  settings, not footage or reports.
- The GPU machine holds footage and derived data. Nothing on it is reachable
  from the internet except its control plane, which expects the gateway's
  bearer token.
- Secrets never live in configuration. Config files carry the **names** of
  environment variables; the values exist only in each machine's environment.
